// To book › Find a lead (BL-092), what it does: every lead, searched by name, phone, email or what they
// asked, open, booked or closed, or only those from an ad; newest first, a page at a time with how many
// there are (leads.search), a moment after the last key. Riverstone's part (LeadSearch.tsx) and
// Blueprint's (FindLead.tsx) draw it.
import { useEffect, useState } from 'react';
import { client } from '../api.ts';
import { errorText } from '../books/ui.tsx';
import type { AdClick } from '../shared/ad-click-model.ts';
import { searchAsked, type SearchStatus } from './lead-search-model.ts';
import type { Lead } from './tobook-model.ts';

export interface FoundLead {
  id: string;
  source: Lead['source'];
  status: 'open' | 'booked' | 'closed';
  name: string | null;
  phone: string | null;
  email: string | null;
  summary: string;
  createdAt: string;
  click: AdClick | null;
}
export interface FoundPage {
  leads: FoundLead[];
  total: number;
  next: string | null;
}
const PAGE = 25;
export const STATUS_WORDS: Record<FoundLead['status'], string> = {
  open: 'Open',
  booked: 'Booked',
  closed: 'Closed',
};
/** Which leads it searches, in the box's words. */
export const SEARCH_STATUSES: { value: SearchStatus; label: string }[] = [
  { value: 'all', label: 'Open, booked and closed' },
  { value: 'open', label: 'Open' },
  { value: 'booked', label: 'Booked' },
  { value: 'closed', label: 'Closed' },
];

export function useLeadSearch() {
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<SearchStatus>('all');
  const [fromAds, setFromAds] = useState(false);
  const [found, setFound] = useState<FoundPage | null>(null);
  const [more, setMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const asked = searchAsked(q, fromAds);
  useEffect(() => {
    setFound(null);
    setError(null);
    if (!asked) return;
    let live = true;
    // a moment after the last key, so each letter isn't a search
    const t = setTimeout(() => {
      client
        .call<FoundPage>('leads.search', { q: q.trim(), status, fromAds, limit: PAGE })
        .then((r) => live && setFound(r))
        .catch((e: unknown) => live && setError(errorText(e, 'Could not search the leads.')));
    }, 300);
    return () => {
      live = false;
      clearTimeout(t);
    };
  }, [q, status, fromAds, asked]);
  const showMore = async () => {
    if (!found?.next) return;
    setMore(true);
    try {
      const r = await client.call<FoundPage>('leads.search', {
        q: q.trim(),
        status,
        fromAds,
        limit: PAGE,
        after: found.next,
      });
      setFound({ leads: [...found.leads, ...r.leads], total: r.total, next: r.next });
    } catch (e) {
      setError(errorText(e, 'Could not get more leads.'));
    } finally {
      setMore(false);
    }
  };
  return { q, setQ, status, setStatus, fromAds, setFromAds, asked, found, more, error, showMore };
}
